import '../../style/chart/RightSideBar.scss';

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {InputSwitch} from 'primereact/inputswitch';
import {OverlayPanel} from 'primereact/overlaypanel';
import {ColorPicker} from 'primereact/colorpicker';
import {Button} from 'primereact/button';

import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {
    DataSource,
    setActiveElement,
    setElements,
    setMineralsElements,
    setSelectedItem,
    setXrfElements,
    setColorGradientXrf,
    setColorGradientMinerals,
    setColorByElementXrf,
    setColorByElementMinerals,
    GradientStop,
} from "../../redux/slices/ChartStateSlice";
import {useGetSettingsQuery, useSaveSettingsMutation} from '../../redux/rtk/settingsApi';
import {elementKey} from '../../functions/drillholeTransform';
import GradientEditor from './GradientEditor';

type Props = {
    showAmountColortable: boolean,
    clickable: boolean,
    multiSelect?: boolean,
    elementMaxValues?: Record<string, number>,
    onElementMaxChange?: (key: string, value: number | null) => void,
    elementMinValues?: Record<string, number>,
    onElementMinChange?: (key: string, value: number | null) => void,
    combinedChart?: boolean,
    combinedChartCount?: number,
    elementChartIndex?: Record<string, number>,
    onElementChartIndexChange?: (key: string, value: number) => void,
    // New optional props for controlling "Hide inactive"
    hideInactive?: boolean,
    onHideInactiveChange?: (value: boolean) => void,
    // When set, this legend manages only the given source (xrf or minerals)
    // and dispatches the matching per-source action. If omitted, falls back
    // to the combined `elements` list and `setElements` dispatch.
    source?: DataSource,
    // Per-view active overrides keyed by elementKey. When provided, toggling
    // the visibility of an element writes to the override map instead of
    // mutating the globally shared element's `active` flag. The effective
    // active state is `activeOverrides[key] ?? el.active ?? true`.
    activeOverrides?: Record<string, boolean>,
    onActiveOverridesChange?: (next: Record<string, boolean>) => void,
}

function ElementLegend(props: Props) {

    const colorTable = useAppSelector((state: any) => state.oldDesignState.colorTable);
    const colorTableXrf = useAppSelector((state: any) => state.oldDesignState.colorTableXrf);
    const colorTableMinerals = useAppSelector((state: any) => state.oldDesignState.colorTableMinerals);
    const colorGradientXrf = useAppSelector((state: any) => state.oldDesignState.colorGradientXrf) as GradientStop[];
    const colorGradientMinerals = useAppSelector((state: any) => state.oldDesignState.colorGradientMinerals) as GradientStop[];
    const colorByElementXrf = useAppSelector((state: any) => state.oldDesignState.colorByElementXrf) as boolean;
    const colorByElementMinerals = useAppSelector((state: any) => state.oldDesignState.colorByElementMinerals) as boolean;
    const activeElement = useAppSelector((state: any) => state.oldDesignState.activeElement);
    const allElements = useAppSelector((state: any) => state.oldDesignState.elements);
    const xrfElements = useAppSelector((state: any) => state.oldDesignState.xrfElements);
    const mineralsElements = useAppSelector((state: any) => state.oldDesignState.mineralsElements);

    const dispatch = useAppDispatch();

    const source = props.source;
    const rawElements: any[] = source === 'xrf'
        ? xrfElements
        : source === 'minerals'
            ? mineralsElements
            : allElements;
    const writeElements = (next: any[]) => {
        if (source === 'xrf') dispatch(setXrfElements(next));
        else if (source === 'minerals') dispatch(setMineralsElements(next));
        else dispatch(setElements(next));
    };

    // Per-view overrides drive what's "active" in this legend instance.
    // The shared element list's `active` is used as a default when no
    // override exists yet.
    const overridesEnabled = props.activeOverrides !== undefined && props.onActiveOverridesChange !== undefined;
    const overrides = props.activeOverrides ?? {};
    const effectiveActive = (el: any): boolean => {
        const key = elementKey(el);
        if (overridesEnabled && Object.prototype.hasOwnProperty.call(overrides, key)) return !!overrides[key];
        return el.active !== false;
    };
    const elements: any[] = useMemo(
        () => overridesEnabled
            ? rawElements.map((el: any) => ({...el, active: effectiveActive(el)}))
            : rawElements,
        [rawElements, overridesEnabled, overrides]
    );
    const setActiveFor = (keys: string[], active: boolean) => {
        if (!overridesEnabled) return;
        const next = {...overrides};
        for (const k of keys) next[k] = active;
        props.onActiveOverridesChange!(next);
    };
    const replaceOverrides = (record: Record<string, boolean>) => {
        if (!overridesEnabled) return;
        props.onActiveOverridesChange!(record);
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [localHideInactive, setLocalHideInactive] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragTargetActive, setDragTargetActive] = useState<boolean | null>(null);
    const draggingRef = useRef(false);

    // Color picker overlay
    const colorPickerRef = useRef<OverlayPanel | null>(null);
    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const [tempColor, setTempColor] = useState<string>('');

    // Settings API hooks for colors per source. When rendered without a source
    // prop (legacy combined mode) we fall back to the first element's source or xrf.
    const resolvedSource: DataSource = source
        ?? (elements[0]?.source === 'minerals' ? 'minerals' : 'xrf');
    const category = useMemo(() => `elementColors_${resolvedSource}`, [resolvedSource]);
    const {data: colorSettings, error: colorSettingsError, isLoading: colorsLoading} = useGetSettingsQuery(
        {category}
    );
    const [saveSettings] = useSaveSettingsMutation();

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
            draggingRef.current = false;
            setDragTargetActive(null);
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, []);

    // Apply color settings from API to current elements, or initialize settings if missing
    useEffect(() => {
        if (!rawElements || rawElements.length === 0) return;
        // If we have settings data as object mapping id->color, apply to elements
        if (colorSettings && typeof colorSettings === 'object' && !Array.isArray(colorSettings)) {
            const mapping = colorSettings as Record<string, string>;
            const next = rawElements.map((el: any) => {
                const key = String(el.id);
                const col = mapping[key];
                return col ? {...el, color: col} : el;
            });
            // Only update if something actually changed, to avoid unnecessary re-renders
            const changed = next.some((el: any, i: number) => el.color !== rawElements[i]?.color);
            if (changed) {
                writeElements(next);
            }
        } else if (colorSettingsError && (colorSettingsError as any)?.status === 404) {
            // No settings found → save current colors as initial state
            const mapping: Record<string, string> = {};
            rawElements.forEach((el: any) => {
                mapping[String(el.id)] = el.color;
            });
            saveSettings({category, data: mapping});
        }
    }, [colorSettings, colorSettingsError, rawElements, category, dispatch, saveSettings]);

    const hideInactive = props.hideInactive ?? localHideInactive;
    const setHideInactive = (val: boolean) => {
        if (props.onHideInactiveChange) props.onHideInactiveChange(val);
        else setLocalHideInactive(val);
    }

    const isMulti = props.multiSelect === true;
    const allActive = useMemo(
        () => elements.length > 0 && elements.every((el: any) => el.active),
        [elements]
    );

    const filteredElements = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return elements.filter((el: any) => {
            if (hideInactive && !el.active) return false;
            if (q === '') return true;
            return el.name?.toLowerCase().includes(q)
                || String(el.id).toLowerCase().includes(q)
                || el.shell?.toLowerCase().includes(q)
                || el.sub_shell?.toLowerCase().includes(q);
        });
    }, [elements, hideInactive, searchQuery]);

    const sortedFilteredElements = filteredElements;

    // --- Multi-select handlers ---
    const handleToggleAll = () => {
        if (!props.clickable) return;
        const newActive = !allActive;
        if (overridesEnabled) {
            setActiveFor(elements.map((el: any) => elementKey(el)), newActive);
        } else {
            const temp = elements.map((el: any) => ({...el, active: newActive}));
            writeElements(temp);
        }
        dispatch(setActiveElement(undefined));
        dispatch(setSelectedItem(undefined));
    };

    const handleMultiToggle = (item: any, forceActive?: boolean) => {
        if (!props.clickable) return;
        const targetActive = forceActive !== undefined ? forceActive : !item.active;
        if (overridesEnabled) {
            setActiveFor([elementKey(item)], targetActive);
        } else {
            const temp = elements.map((el: any) =>
                el.id === item.id ? {...el, active: targetActive} : {...el}
            );
            writeElements(temp);
        }
        dispatch(setActiveElement(undefined));
        dispatch(setSelectedItem(undefined));
    };

    const handleDragStart = (item: any) => {
        if (!props.clickable || !isMulti) return;
        const newActive = !item.active;
        setIsDragging(true);
        draggingRef.current = true;
        setDragTargetActive(newActive);
        handleMultiToggle(item, newActive);
    };

    const handleDragEnter = (item: any) => {
        if (draggingRef.current && dragTargetActive !== null) {
            handleMultiToggle(item, dragTargetActive);
        }
    };

    // --- Single-select handler (original behavior) ---
    const handleSingleClick = (item: any) => {
        if (!props.clickable) return;
        dispatch(setSelectedItem(undefined));

        const temp = JSON.parse(JSON.stringify(elements));

        // If clicking the only active element → re-activate all
        let setAllActive = false;
        for (let i = 0; i < temp.length; i++) {
            if (item.active && !temp[i].active) {
                setAllActive = true;
                break;
            }
        }

        for (let i = 0; i < temp.length; i++) {
            if (setAllActive) {
                temp[i].active = true;
            } else {
                const active = item.id === temp[i].id;
                temp[i].active = active;
                if (active) {
                    dispatch(setActiveElement(temp[i]));
                }
            }
        }

        if (setAllActive) {
            dispatch(setActiveElement(undefined));
        }

        if (overridesEnabled) {
            const next: Record<string, boolean> = {};
            for (const el of temp) next[elementKey(el)] = !!el.active;
            replaceOverrides(next);
        } else {
            writeElements(temp);
        }
    };

    return (
        <div className={'legend col-6 md:col-12 p-0'} style={!props.showAmountColortable ? {height: '100%'} : {}}>
            <div>
                <div style={{position: 'relative', marginBottom: '6px'}}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '4px 24px 4px 8px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxSizing: 'border-box',
                            outline: 'none',
                        }}
                    />
                    {searchQuery && (
                        <span
                            onClick={() => setSearchQuery('')}
                            style={{
                                position: 'absolute',
                                right: '6px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                color: '#999',
                                fontSize: '12px',
                                lineHeight: 1,
                            }}>✕</span>
                    )}
                </div>

                <div className={'flex align-items-center mb-2 pb-2'}
                     style={{borderBottom: '1px solid #ddd', gap: '8px'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', cursor: 'pointer'}}>
                        <input type="checkbox" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)}/>
                        Hide inactive
                    </label>
                    {props.clickable && isMulti && (
                        <InputSwitch checked={allActive} onChange={handleToggleAll}
                                     style={{transform: 'scale(0.7)', transformOrigin: 'right center', cursor: 'pointer', marginLeft: 'auto'}}/>
                    )}
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                    {sortedFilteredElements.map((item: any, index: number) => {
                        return <div key={'ele_' + item.name + '_' + index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '3px 4px',
                                        cursor: props.clickable && !isMulti ? 'pointer' : 'default',
                                        borderRadius: '4px',
                                        opacity: item.active ? 1 : 0.4,
                                        userSelect: isDragging ? 'none' : 'auto',
                                    }}
                                    onMouseEnter={() => isMulti && handleDragEnter(item)}
                                    onClick={() => !isMulti && handleSingleClick(item)}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '3px',
                                background: item.color,
                                flexShrink: 0,
                                filter: item.active ? undefined : 'grayscale(100%)',
                                border: '1px solid #999',
                                cursor: 'pointer',
                            }}
                                 onClick={(e) => {
                                     e.stopPropagation();
                                     setEditingItemId(item.id);
                                     // ColorPicker von PrimeReact verwendet HEX ohne '#'
                                     const hex = (item.color || '').replace('#', '');
                                     setTempColor(hex);
                                     colorPickerRef.current?.toggle(e);
                                 }}
                            />
                            <div style={{flex: 1, minWidth: 0, fontSize: '11px', lineHeight: '1.3', display: 'flex', flexDirection: 'column'}}>
                                <div>
                                    <span style={{fontWeight: 600}}>{item.name}</span>
                                    <span style={{color: '#999', marginLeft: '4px', fontSize: '10px'}}>
                                        {item.shell
                                            ? '(' + item.id + '/' + item.shell + '/' + item.sub_shell + ')'
                                            : '(' + item.id + ')'}
                                    </span>
                                </div>
                                <div style={{display: 'flex', gap: '4px', marginTop: '4px', alignItems: 'center'}}>
                                    {props.onElementMinChange && !props.combinedChart && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
                                            <span style={{fontSize: '9px', color: '#666'}}>Min:</span>
                                            <input
                                                type="number"
                                                placeholder="Auto"
                                                value={props.elementMinValues?.[elementKey(item)] ?? ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const val = raw === '' ? null : parseFloat(raw);
                                                    if (val === null || !isNaN(val)) {
                                                        props.onElementMinChange!(elementKey(item), val);
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    width: '45px',
                                                    padding: '1px 3px',
                                                    fontSize: '10px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    outline: 'none',
                                                    background: 'transparent',
                                                }}
                                            />
                                        </div>
                                    )}
                                    {props.onElementMaxChange && !props.combinedChart && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
                                            <span style={{fontSize: '9px', color: '#666'}}>Max:</span>
                                            <input
                                                type="number"
                                                min={0}
                                                placeholder="Auto"
                                                value={props.elementMaxValues?.[elementKey(item)] ?? ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const val = raw === '' ? null : parseFloat(raw);
                                                    if (val === null || (!isNaN(val) && val >= 0)) {
                                                        props.onElementMaxChange!(elementKey(item), val);
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    width: '45px',
                                                    padding: '1px 3px',
                                                    fontSize: '10px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    outline: 'none',
                                                    background: 'transparent',
                                                }}
                                            />
                                        </div>
                                    )}
                                    {props.combinedChart && props.combinedChartCount != null && props.combinedChartCount > 1 && props.onElementChartIndexChange && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
                                            <span style={{fontSize: '9px', color: '#666'}}>Chart:</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={props.combinedChartCount}
                                                placeholder="Idx"
                                                value={props.elementChartIndex?.[elementKey(item)] ?? 1}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    if (!isNaN(val) && val >= 1 && val <= (props.combinedChartCount ?? 1)) {
                                                        props.onElementChartIndexChange!(elementKey(item), val);
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    width: '35px',
                                                    padding: '1px 3px',
                                                    fontSize: '10px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    outline: 'none',
                                                    background: 'transparent',
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {props.clickable && isMulti && (
                                <div onMouseDown={() => handleDragStart(item)}>
                                    <InputSwitch checked={item.active}
                                                 onChange={() => {}}
                                                 style={{transform: 'scale(0.7)'}}/>
                                </div>
                            )}
                        </div>
                    })}
                </div>

                {/* Overlay Color Picker */}
                <OverlayPanel ref={colorPickerRef} dismissable onHide={() => setEditingItemId(null)}>
                    <div className="flex flex-column gap-3 p-1">
                        <ColorPicker
                            value={tempColor}
                            onChange={(e) => {
                                if (e.value) {
                                    setTempColor(e.value as string);
                                }
                            }}
                            inline
                        />
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span style={{fontSize: '12px', color: '#666'}}>#</span>
                            <input
                                type="text"
                                maxLength={6}
                                value={tempColor}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9a-fA-F]/g, '');
                                    setTempColor(val);
                                }}
                                style={{
                                    width: '80px',
                                    padding: '4px 6px',
                                    fontSize: '12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    outline: 'none',
                                    textTransform: 'uppercase',
                                }}
                            />
                            <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '3px',
                                background: tempColor.length === 6 ? `#${tempColor}` : 'transparent',
                                border: '1px solid #999',
                                flexShrink: 0,
                            }}/>
                        </div>
                        <div className="flex justify-content-between gap-2">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text p-button-sm p-button-secondary"
                                onClick={() => colorPickerRef.current?.hide()}
                            />
                            <Button
                                label="Ok"
                                icon="pi pi-check"
                                className="p-button-sm"
                                onClick={() => {
                                    if (editingItemId != null) {
                                        const newColor = `#${tempColor}`;
                                        // Color is shared across views, so write back to the
                                        // raw element list (without baking in per-view active).
                                        const updated = rawElements.map((el: any) =>
                                            el.id === editingItemId ? {...el, color: newColor} : el
                                        );
                                        writeElements(updated);

                                        // Persistiere Mapping in API (global)
                                        const mapping: Record<string, string> = {};
                                        updated.forEach((el: any) => {
                                            mapping[String(el.id)] = el.color;
                                        });
                                        saveSettings({category, data: mapping});
                                    }
                                    colorPickerRef.current?.hide();
                                }}
                            />
                        </div>
                    </div>
                </OverlayPanel>

                {props.showAmountColortable && (() => {
                    const gradient = resolvedSource === 'minerals' ? colorGradientMinerals : colorGradientXrf;
                    const onGradientChange = (next: GradientStop[]) => {
                        if (resolvedSource === 'minerals') dispatch(setColorGradientMinerals(next));
                        else dispatch(setColorGradientXrf(next));
                    };
                    const displayTable = resolvedSource === 'xrf'
                        ? (colorTableXrf.length > 0 ? colorTableXrf : colorTable)
                        : (colorTableMinerals.length > 0 ? colorTableMinerals : colorTable);
                    // Derive a max value from the bucketed colorTable produced by Chart.
                    const maxValue = displayTable.length > 0
                        ? Number(displayTable[displayTable.length - 1].last)
                        : 1;
                    const tableLabel = activeElement?.source === resolvedSource
                        ? activeElement.name
                        : resolvedSource === 'xrf' ? 'XRF Amount' : 'Minerals Amount';
                    const colorByElement = resolvedSource === 'minerals' ? colorByElementMinerals : colorByElementXrf;
                    const onColorByElementChange = (v: boolean) => {
                        if (resolvedSource === 'minerals') dispatch(setColorByElementMinerals(v));
                        else dispatch(setColorByElementXrf(v));
                    };
                    return (
                        <GradientEditor
                            gradient={gradient}
                            onChange={onGradientChange}
                            maxValue={maxValue || 1}
                            label={tableLabel}
                            colorByElement={colorByElement}
                            onColorByElementChange={onColorByElementChange}
                        />
                    );
                })()}
            </div>
        </div>
    )
}

export default ElementLegend
